import { Component, OnInit } from '@angular/core';
import Highcharts from 'highcharts';
import { CacheService } from 'projects/common/src/app/services';
import { ParameterHandlerService } from 'projects/common/src/app/services/parameter-handler.service';
import { LanguageService } from 'projects/common/src/app/services/parameters';
import { InputDateFormatPipe } from 'src/app/modules/core/pipes/input-date-format.pipe';
import { BooleanStateService } from 'src/app/modules/core/services/boolean-state.service';
import { MachineContextService } from 'src/app/modules/core/services/parameter/machine-context/machine-context.service';
import { StateSidebarService } from 'src/app/modules/sidebar/services/state-sidebar.service';
import { ScreenViewService } from 'src/app/modules/widget/services/screen-view.service';
import { CommunicationService } from 'src/app/services/communication';
import { IProductionTonsHour } from '../../../interfaces/sp/productionbyTonsHour';
import { WidgetDataService } from '../../../services/widget-data/widget-data.service';
import { DashboardWidgetChartComponent } from '../../dashboard-widget-chart/dashboard-widget-chart.component';

@Component({
  selector: 'app-production-tons-by-hour',
  templateUrl: './production-tons-by-hour.component.html',
  styleUrls: ['./production-tons-by-hour.component.scss'],
})
export class ProductionTonsByHourComponent extends DashboardWidgetChartComponent implements OnInit {
  public inputDate: string;
  public zoneName: string;
  public productionTonsHour: IProductionTonsHour[];
  public displayMessage = false;
  public hasData = false;

  constructor(
    protected progressBarService: BooleanStateService,
    protected communication: CommunicationService,
    protected parameterService: ParameterHandlerService,
    protected sidebarService: StateSidebarService,
    protected cacheService: CacheService,
    protected machineContext: MachineContextService,
    protected formatDatePipe: InputDateFormatPipe,
    protected languageService: LanguageService,
    protected widgetDataService: WidgetDataService,
    protected screenViewService: ScreenViewService,
  ) {
    super(progressBarService, communication, parameterService, sidebarService, cacheService, machineContext, screenViewService);
    this.initRequestParams();
  }

  public ngOnInit(): void {
    super.ngOnInit();
  }

  /**
   * Init request params
   */
  protected initRequestParams(): void {
    this.params = {
      hasShift: true,
      hasCompany: true,
      hasDate: true,
      hasMachineType: true,
      hasZone: true,
    };
  }

  /**
   * drawView Chart
   */
  public drawView(chartOptions?: any): void {
    this.hasData = !!chartOptions.length;

    if (chartOptions.length) {
      this.productionTonsHour = chartOptions;

      const machineType = this.parameterService.parameters.item('machine-context').value || 'no disponible';
      const user = this.parameterService.parameters.item('user-data').value;
      const date = this.parameterService.parameters.item('date').value;
      const legendLabel = this.languageService.message('tons-by-hour');
      const shiftValue = this.parameterService.parameters.item('shift').value;
      const shiftName = shiftValue.name;
      const shift = this.languageService.message('shift_');
      const heightStick = 400;
      const machineName = this.productionTonsHour.map((m) => m.machineName);
      const serie = this.productionTonsHour.map((tonshour) => +tonshour.tonHrs);

      const shiftTooltip = this.languageService.message('shift_').toUpperCase();

      this.zoneName = this.cacheService.session('selectedZone').zoneName;

      const dataSerie = {
        dataLabels: {
          enabled: true,
          style: {
            color: '#707070',
            fontSize: '11px',
            fontWeight: 600,
          },
          crop: false,
          overflow: 'none',
        },
        meditionUnit: '',
        type: 'bar',
        data: serie,
        color: 'rgba(34, 217, 131, 0.5)',
        name: legendLabel,
      };
      const series = [dataSerie];
      const data = {
        chart: {
          type: 'bar',
          height: this.productionTonsHour.length > 12 ? 200 + this.productionTonsHour.length * 20 : heightStick,
          backgroundColor: '#F8F8F8',
          color: 'rgba(34, 217, 131, 0.5)',
          style: {
            fontFamily: 'Open Sans',
          },
        },
        title: {
          text: '',
        },
        subtitle: {
          text: `${this.languageService.message('company')}: ${user.company.name},
                ${this.languageService.message('device')}: ${this.languageService.message(machineType.name)},
                ${this.languageService.message('date')}: ${this.formatDatePipe.transform(date)},
                ${this.languageService.message('shift_')}: ${shiftName}`,
        },
        credits: {
          enabled: false,
        },
        xAxis: {
          title: {
            text: this.languageService.message('equipments').toUpperCase(),
            style: {
              fontSize: '12px',
              color: '#707070',
              fontWeight: '600',
            },
          },
          categories: machineName,
          gridLineWidth: 1,
          labels: {
            useHTML: true,
            style: {
              fontSize: '12px',
              color: '#707070',
              fontWeight: '400',
              paddingTop: 0,
              paddingBottom: 0,
            },
          },
        },
        yAxis: [
          {
            title: {
              text: this.languageService.message('tons-by-hour').toUpperCase(),
              style: {
                fontSize: '12px',
                color: '#707070',
                fontWeight: '600',
              },
            },
            labels: {
              style: {
                fontSize: '10px',
                color: '#707070',
                fontWeight: '400',
              },
            },
            lineWidth: 1,
            lineColor: '#707070',
          },
          {
            opposite: true,
            lineWidth: 1,
            lineColor: '#707070',
            title: {
              text: '',
            },
          },
        ],
        legend: {
          useHTML: true,
          squareSymbol: false,
          align: 'center',
          verticalAlign: 'top',
          symbolHeight: 0.001,
          symbolWidth: 0.001,
          symbolRadius: 0.001,
          labelFormatter: () => {
            return `<div style=" height: 20px; display: flex;" >
          <svg width="36" height="18" style=" float: left;">
            <rect width="30" height="12" x="2" y="2"
            style="fill: #22D98380; stroke-width: 2px !important;
            stroke: #22D983 !important;"/>
          </svg>
          <span style="font-size: 12px; font-family: Open Sans; font-weight: 500;
          color: #433E3E; float: left;">${legendLabel}</span>
         </div>`;
          },
        },
        tooltip: {
          backgroundColor: 'rgb(51, 51, 51, 1)',
          borderColor: '#333333',
          borderRadius: 2,
          style: {
            color: '#FFFFFF',
          },
          useHTML: true,
          formatter(): string {
            let divSymbol = '';
            divSymbol = `<div style="background-color: #22d983;
                      width: 30px;
                      height: 12px;
                      border: 1px solid #22d983;
                      float: left;
                      margin-top: 2px">
                  </div>`;

            return `<div style="width: 200px;
                          font-size: 10px;
                          color: #FFFFFF;">
                <strong style="font-weight: 700;"> ${shiftTooltip} ${shift && shiftName.toUpperCase()}</strong> <br>
                <span style="font-weight: 400;">${this.x} </span> <br>
                ${divSymbol} &nbsp;
                <span style="font-weight: 400;">${this.y} ${legendLabel} </span>
              </div>`;
          },
        },
        plotOptions: {
          series: {
            color: 'rgba(34, 217, 131, 0.5)',
            borderWidth: 2,
            borderColor: '#22D983',
            events: {
              legendItemClick: (e) => {
                e.preventDefault();
              },
            },
          },
        },
        series,
      };

      if (!this.chartTarget?.nativeElement) {
        return;
      }

      this.chart = Highcharts.chart(this.chartTarget.nativeElement, data as any, (chart) => {
        if (!this.hasData) {
          const newX = chart.plotWidth / 3 + chart.plotLeft;
          const newY = chart.plotHeight / 2 + chart.plotTop;
          chart.renderer
            .text('La solicitud no arrojó resultados', newX, newY)
            .css({
              color: '#4572A7',
              fontSize: '24px',
            })
            .add();
        }
      });
      window.dispatchEvent(new Event('resize'));
    } else {
      this.displayMessage = true;
    }
  }
}
